import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/Field';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { PageContainer } from '../../app/AppShell';
import { useAuthStore } from '../../app/authStore';
import { errorMessage } from '../../lib/api';
import { validatePassword } from '@shared/validate';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const forced = useAuthStore((state) => state.mustChangePassword);
  const changePassword = useAuthStore((state) => state.changePassword);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ruleError = next === '' ? null : validatePassword(next);
  const mismatch = confirm !== '' && next !== confirm ? '两次输入的新口令不一致' : null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (ruleError || mismatch) return;
    setSubmitting(true);
    setError(null);
    try {
      await changePassword(current, next);
      navigate('/fleet', { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageContainer className="max-w-xl">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">修改口令</h1>
        {forced ? <Badge tone="warning">必须完成</Badge> : null}
      </div>

      {forced ? (
        <div className="rounded-lg border border-warning/60 bg-warning-soft px-3.5 py-3 text-sm text-fg">
          当前口令是管理员分配的临时口令，首次登录必须修改后才能继续使用平台。
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle subtitle={user ? `账号：${user.email}` : undefined}>账号安全</CardTitle>
        </CardHeader>
        <CardBody>
          <form className="flex flex-col gap-3.5" onSubmit={submit} noValidate>
            <TextInput
              label="当前口令"
              required
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />

            <TextInput
              label="新口令"
              required
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              error={ruleError ?? undefined}
              hint="至少 8 位，需同时包含字母与数字"
            />

            <TextInput
              label="确认新口令"
              required
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              error={mismatch ?? undefined}
            />

            {error ? <p className="text-xs text-critical">{error}</p> : null}

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={current === '' || next === '' || confirm === '' || Boolean(ruleError) || Boolean(mismatch)}
              >
                提交
              </Button>
              {forced ? null : (
                <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                  取消
                </Button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>
    </PageContainer>
  );
}
